"use client";

import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const DRAG_THRESHOLD_PX = 4;

interface HorizontalDragScrollOptions {
  /** Skip drag handling for touch pointers — native overflow scroll is smoother. */
  ignoreTouch?: boolean;
}

interface HorizontalDragScrollResult<T extends HTMLElement> {
  ref: RefObject<T | null>;
  isDragging: boolean;
  onPointerDown: (event: ReactPointerEvent<T>) => void;
  onPointerMove: (event: ReactPointerEvent<T>) => void;
  onPointerUp: (event: ReactPointerEvent<T>) => void;
  onPointerCancel: (event: ReactPointerEvent<T>) => void;
  onClickCapture: (event: React.MouseEvent<T>) => void;
}

/**
 * Pointer-driven horizontal drag scrolling for overflow containers.
 * Mouse: click-drag pans the row. Touch: defaults to native overflow scroll
 * (set ignoreTouch: false to handle touch via pointer events too).
 */
export function useHorizontalDragScroll<T extends HTMLElement>(
  options: HorizontalDragScrollOptions = {}
): HorizontalDragScrollResult<T> {
  const { ignoreTouch = true } = options;
  const ref = useRef<T | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    active: false,
    pointerId: -1,
    startX: 0,
    startScrollLeft: 0,
    didDrag: false,
  });

  const resetDrag = useCallback(() => {
    dragRef.current = {
      active: false,
      pointerId: -1,
      startX: 0,
      startScrollLeft: 0,
      didDrag: false,
    };
    setIsDragging(false);
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<T>) => {
      if (event.button !== 0) return;
      if (ignoreTouch && event.pointerType === "touch") return;

      const el = ref.current;
      if (!el) return;

      dragRef.current = {
        active: true,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: el.scrollLeft,
        didDrag: false,
      };
    },
    [ignoreTouch]
  );

  const onPointerMove = useCallback((event: ReactPointerEvent<T>) => {
    const el = ref.current;
    const drag = dragRef.current;
    if (!el || !drag.active || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    if (!drag.didDrag && Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;

    if (!drag.didDrag) {
      drag.didDrag = true;
      el.setPointerCapture(event.pointerId);
      setIsDragging(true);
    }
    el.scrollLeft = drag.startScrollLeft - deltaX;
  }, []);

  const onPointerUp = useCallback((event: ReactPointerEvent<T>) => {
    const el = ref.current;
    const drag = dragRef.current;
    if (!el || !drag.active || event.pointerId !== drag.pointerId) return;

    if (el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
    drag.active = false;
  }, []);

  const onPointerCancel = useCallback(
    (event: ReactPointerEvent<T>) => {
      const el = ref.current;
      if (el?.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      resetDrag();
    },
    [resetDrag]
  );

  const onClickCapture = useCallback((event: React.MouseEvent<T>) => {
    if (dragRef.current.didDrag) {
      event.preventDefault();
      event.stopPropagation();
      dragRef.current.didDrag = false;
    }
  }, []);

  useEffect(() => {
    const onWindowPointerUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag.active || event.pointerId !== drag.pointerId) return;
      drag.active = false;
      setIsDragging(false);
    };
    const onWindowPointerCancel = (event: PointerEvent) => {
      if (event.pointerId === dragRef.current.pointerId) resetDrag();
    };

    window.addEventListener("pointerup", onWindowPointerUp);
    window.addEventListener("pointercancel", onWindowPointerCancel);

    return () => {
      window.removeEventListener("pointerup", onWindowPointerUp);
      window.removeEventListener("pointercancel", onWindowPointerCancel);
      resetDrag();
    };
  }, [resetDrag]);

  return {
    ref,
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClickCapture,
  };
}
